import SellingCompaniesAPI, { SellingCompany } from 'core/selling-companies';
import {
  SerializedError,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';

import { DEFAULT_SELLING_COMPANY } from 'config';
import { CustomError } from 'core/errors';
import { Nullable } from 'utils/types';
import { RootState } from 'store/root-reducer';

export type SellingCompanies = {
  entities: ReturnType<typeof entityAdapter.getInitialState>;
  loading: boolean;
  error: Nullable<CustomError>;
  currentCompany: SellingCompany['id'];
};

export const entityAdapter = createEntityAdapter<SellingCompany>({
  selectId: (company) => company.id,
  sortComparer: (a, b) => a.id.localeCompare(b.id),
});

const initialState: SellingCompanies = {
  entities: entityAdapter.getInitialState(),
  loading: false,
  error: null,
  currentCompany: '',
};

// ASYNC thunks
const searchSellingCompanies = createAsyncThunk<SellingCompany[], void>(
  'sellingCompanies/searchSellingCompanies',
  async (params) => {
    try {
      return await SellingCompaniesAPI.getSellingCompanies();
    } catch (err) {
      const error: CustomError =
        err?.customError || CustomError.createClientError();
      const serialize: SerializedError = {
        name: error.code,
        message: error.description,
        code: `${error.statusCode}`,
        stack: JSON.stringify(error),
      };
      throw serialize;
    }
  }
);

const sellingCompanySlice = createSlice({
  name: 'sellingCompanies',
  initialState,
  reducers: {
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchSellingCompanies.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchSellingCompanies.fulfilled, (state, action) => {
      const companyId = action.payload.find(
        ({ name }) =>
          name?.toLocaleUpperCase() === DEFAULT_SELLING_COMPANY.toUpperCase()
      )?.id;
      state.currentCompany = companyId ?? '';
      state.loading = false;
      entityAdapter.setAll(state.entities, action.payload);
    });
    builder.addCase(searchSellingCompanies.rejected, (state, { error }) => {
      state.error = error?.stack ? JSON.parse(error.stack) : null;
      state.loading = false;
    });
  },
});

const { reducer, actions } = sellingCompanySlice;

export const { resetState: resetSellingCompanyState } = actions;

export const SellingCompaniesActions = {
  searchSellingCompanies,
};

const entitySelectors = entityAdapter.getSelectors<RootState>(
  (state) => state.sellingCompanies.entities
);

export const selectSellingCompaniesLoading = (state: RootState) =>
  state.sellingCompanies.loading;

export const selectSellingCompaniesError = (state: RootState) =>
  state.sellingCompanies.error;
export const selectCurrentCompanyId = (state: RootState) =>
  state.sellingCompanies.currentCompany;

export const {
  selectAll: selectAllSellingCompanies,
  selectTotal: selectTotalSellingCompanies,
  selectById: selectSellingCompanyById,
} = entitySelectors;

export { searchSellingCompanies };

export default reducer;
